import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n/hooks";
import { useCurrencyIcon, useCurrencySymbol } from "@/hooks/useUtils";
import { Button } from "@/components/Base/Button";
import { useUserDataFlattened } from "@/modules";
import {
  usePlatformLogo,
  useEnableDownloadAppFloatingOnHomeTop,
  useLoginRegisterConfig,
} from "@/modules/homeDisplay/hooks";
import { useNavigation, ROUTES } from "@/utils/navigation";
import { openLoginRegisterPopup } from "@/components/LoginRegisterPopup";
import { openDepositPopup } from "@/components/DepositPopup/triggerDepositPopup";
import { PromoBubble } from "@/components/PromoBubble";
import { useMessageNotification } from "@/modules/notification";
import { useUserStore } from "@/modules/user/stores";
import { DownloadAppBar } from "@/components/DownloadAppBar";
import { isDownloadAppBarSessionClosed } from "@/utils/downloadAppBarSession";
import "./Header.scss";
import type { HeaderProps } from "../../Header.types";
import { utils } from "@/index";
import IconSearch from "@/assets/icons/ic-search.svg";
import IconSupport from "@/assets/icons/support-icon.png";
import IconMessage from "@/assets/icons/message.png";
import IconLanguage from "@/assets/icons/ic-language.svg";

const ICON_URLS = {
  search: IconSearch,
  service: IconSupport,
  message: IconMessage,
  language: IconLanguage,
};

export function Header(props: HeaderProps) {
  const {
    className = "",
    pageType = "home",
    title,
    rightIcon,
    hideBackButton = false,
    onLanguageClick,
    onMessageClick,
    onServiceClick,
    onSearchClick,
    messageUnreadCount: messageUnreadCountProp,
  } = props;

  const { t } = useI18n();
  const routerNavigate = useNavigate();
  const { navigate } = useNavigation();
  // 余额必须取合并了 realtime 的数据：user_info 拆分后余额只进 realtime 切片，
  // 直接读 state.userInfo 拿不到 loadUserRealtime 的更新，顶部金额会一直不变。
  const { isAuthenticated, data: userInfo } = useUserDataFlattened();
  const headerRef = useRef<HTMLElement>(null);
  const enableFloatingOnHomeTop = useEnableDownloadAppFloatingOnHomeTop();

  // 下载 App 顶栏（DownloadAppBar，position:fixed）在 prime 首页同样挂载（classic 挂载在
  // Header variant 内，prime 此前遗漏 → az-sa / rosetest 等 prime 预设首页看不到下载条）。
  // 组件自带 config / 路由 / 会话关闭三重显隐门；这里只额外跟踪可见态以决定是否渲染等高占位块
  // （__download-slot）——沉浸式浮动顶栏下用它把 header 内容让到下载条之下，与 classic 一致。
  const [downloadAppBarVisible, setDownloadAppBarVisible] = useState(() => {
    if (utils.isApp() || utils.updateCheckIsPwa() || !enableFloatingOnHomeTop)
      return false;
    return !isDownloadAppBarSessionClosed();
  });
  useEffect(() => {
    const handler = (e: Event) =>
      setDownloadAppBarVisible((e as CustomEvent).detail);
    window.addEventListener("downloadAppBarChange", handler);
    return () => window.removeEventListener("downloadAppBarChange", handler);
  }, []);
  const { messageUnreadCount: notificationUnreadCount } =
    useMessageNotification();
  const messageUnreadCount =
    messageUnreadCountProp ??
    (pageType === "profile" ? notificationUnreadCount : 0);

  // 用户余额相关
  const loadUserInfo = useUserStore(state => state.loadUserInfo);
  const [isRefreshingBalance, setIsRefreshingBalance] = useState(false);

  const handleRefreshBalance = useCallback(async () => {
    if (isRefreshingBalance) return;
    setIsRefreshingBalance(true);
    try {
      await loadUserInfo();
    } catch {
      // 刷新失败静默忽略，保持旧余额显示
    } finally {
      setTimeout(() => setIsRefreshingBalance(false), 300);
    }
  }, [loadUserInfo, isRefreshingBalance]);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const sync = () => {
      const h = el.getBoundingClientRect().height;
      document.documentElement.style.setProperty("--header-height", `${h}px`);
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // 滚动状态监听 - 使用与 GameBrowser 相同的检测逻辑
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    let container: Element | null = null;
    let rafId: number;

    const checkScrollState = () => {
      const header = headerRef.current;
      if (!header) return;

      const headerBottom = header.getBoundingClientRect().bottom;

      // 方法1：检测 superace-hero-bg 是否滚出 header 区域
      const heroBg = document.querySelector(".superace-hero-bg");
      if (heroBg) {
        const heroBgRect = heroBg.getBoundingClientRect();
        setIsScrolled(heroBgRect.bottom <= headerBottom);
        return;
      }

      // 方法2：检测 categories 是否吸顶（与 GameBrowser 相同的逻辑）
      const categories = document.querySelector(".game-browser__categories");
      if (categories) {
        const catRect = categories.getBoundingClientRect();
        const stuck = catRect.top <= headerBottom + 2;
        setIsScrolled(stuck);
        return;
      }

      // 方法3：直接检测 scrollTop
      if (container && container.scrollTop > 0) {
        setIsScrolled(true);
      }
    };

    const setupListener = () => {
      container = document.querySelector(".mobile-home-page");
      if (container) {
        container.addEventListener("scroll", checkScrollState, {
          passive: true,
        });
        checkScrollState();
        return true;
      }
      return false;
    };

    // 持续尝试设置监听，直到成功
    const trySetup = () => {
      if (!setupListener()) {
        rafId = requestAnimationFrame(trySetup);
      }
    };
    trySetup();

    return () => {
      cancelAnimationFrame(rafId);
      container?.removeEventListener("scroll", checkScrollState);
    };
  }, []);

  const platformLogo = usePlatformLogo();
  const logoSrc = utils.getImageUrl(platformLogo?.logoUrl || "");

  // 注册按钮上的首充优惠气泡文案（后台配置，空串时 PromoBubble 自行返回 null）
  const loginRegisterConfig = useLoginRegisterConfig();
  // 货币图标／符号跟随后台的货币配置（startup.currencies 上的 icon / label），
  // 不再用写死的比索图 —— 换币种的渠道会显示错的货币。口径同个人中心
  // .balance-card__amount-text：有配图用图，没配图退回文字符号。
  const currencyIcon = useCurrencyIcon();
  const currencySymbol = useCurrencySymbol();
  const homeRegisterPromoText =
    loginRegisterConfig?.homeRegisterBtnFirstChargeText?.trim() ?? "";

  const handleServiceClick = () => {
    if (onServiceClick) {
      onServiceClick();
    } else {
      navigate(`${ROUTES.MESSAGE_CENTER}?tabIndex=1`);
    }
  };

  const handleSearchClick = () => {
    if (onSearchClick) {
      onSearchClick();
    } else {
      navigate("/game-search");
    }
  };

  const handleMessageClick = () => {
    if (onMessageClick) {
      onMessageClick();
    } else {
      navigate(`${ROUTES.MESSAGE_CENTER}?tabIndex=2`);
    }
  };

  const handlePromoClick = () => {
    if (isAuthenticated) {
      navigate(ROUTES.MESSAGE_CENTER);
    } else {
      openLoginRegisterPopup({ defaultMode: "login" });
    }
  };

  // 促销票信封图标：容器由 CSS 消费 --color-gradient-primary 跟随主题，
  // 信封本身用 --color-text-btn-primary（品牌上的对比色）随主题着色，
  // 取代原来固定配色的 promo-ticket.png。
  const renderPromoIcon = () => (
    <svg
      className="mobile-header__promo-icon"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="5" width="18" height="14" rx="3" />
      <path d="m3.5 7.5 7.3 5.1a2 2 0 0 0 2.4 0l7.3-5.1" />
    </svg>
  );

  // 渲染已登录状态的钱包组件
  const renderWalletItem = () => {
    const balance = userInfo?.balanceDetail?.balance ?? userInfo?.balance ?? 0;

    return (
      <>
        <div className="wallet-item">
          {/* 左侧余额信息 */}
          <div
            className="wallet-item__balances"
            onClick={() => navigate(ROUTES.ACCOUNT_DETAILS)}
          >
            <div className="wallet-item__balance-row">
              {currencyIcon ? (
                <img
                  src={utils.getImageUrl(currencyIcon)}
                  alt={currencySymbol}
                  className="wallet-item__icon"
                  draggable={false}
                />
              ) : (
                <span className="wallet-item__currency">{currencySymbol}</span>
              )}
              <span className="wallet-item__amount">{balance.toFixed(2)}</span>
            </div>
          </div>

          {/* 中间刷新按钮 */}
          <button
            className={`wallet-item__refresh-btn ${isRefreshingBalance ? "wallet-item__refresh-btn--loading" : ""}`}
            onClick={handleRefreshBalance}
            disabled={isRefreshingBalance}
            aria-label="Refresh balance"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="1em"
              fill="none"
              viewBox="0 0 41 41"
              stroke="currentColor"
              strokeWidth="4"
              className="wallet-item__refresh-icon"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M34.532 20.087c0 7.974-6.471 14.445-14.445 14.445-7.973 0-12.84-8.031-12.84-8.031m0 0h6.528m-6.529 0v7.222M5.643 20.087A14.413 14.413 0 0 1 20.087 5.643c9.635 0 14.445 8.03 14.445 8.03m0 0V6.453m0 7.222h-6.414"
              />
            </svg>
          </button>

          {/* 右侧充值按钮：用金色渐变（secondary）按钮样式 */}
          <Button
            variant="secondary"
            size="sm"
            className="wallet-item__deposit-btn"
            onClick={() => openDepositPopup()}
          >
            {t("deposit.title")}
          </Button>
        </div>

        {/* 促销票图标 */}
        <button
          className="mobile-header__promo-btn"
          aria-label="Promo"
          onClick={handlePromoClick}
        >
          {renderPromoIcon()}
        </button>
      </>
    );
  };

  const renderIcons = () => {
    if (isAuthenticated) {
      switch (pageType) {
        case "profile":
          return (
            <div className="mobile-header__right-icons">
              <button
                className="mobile-header__message-icon"
                onClick={handleMessageClick}
                aria-label={t("common.messageCenter")}
              >
                <img
                  src={ICON_URLS.message}
                  alt=""
                  className="mobile-header__auth-bar-icon-img"
                  draggable={false}
                />
                {messageUnreadCount > 0 && (
                  <span className="mobile-header__badge">
                    {messageUnreadCount > 99 ? "99+" : messageUnreadCount}
                  </span>
                )}
              </button>
              <button
                className="mobile-header__service-icon"
                onClick={handleServiceClick}
                aria-label={t("profile.support")}
              >
                <img
                  src={ICON_URLS.service}
                  alt=""
                  className="mobile-header__auth-bar-icon-img"
                  draggable={false}
                />
              </button>
            </div>
          );
        case "subpage":
          return (
            <div className="mobile-header__right-icons">
              <button
                className="mobile-header__service-icon"
                onClick={handleServiceClick}
                aria-label={t("profile.support")}
              >
                <img
                  src={ICON_URLS.service}
                  alt=""
                  className="mobile-header__auth-bar-icon-img"
                  draggable={false}
                />
              </button>
            </div>
          );
        case "home":
        default:
          // 首页已登录：显示 wallet-item
          return renderWalletItem();
      }
    }

    // 未登录状态
    if (pageType === "profile") {
      // profile 页面未登录时：不显示登录按钮，显示语言、消息、客服 icon
      return (
        <div className="mobile-header__right-icons">
          <button
            className="mobile-header__language-icon"
            onClick={onLanguageClick}
            aria-label={t("profile.language")}
          >
            <img
              src={ICON_URLS.language}
              alt=""
              className="mobile-header__auth-bar-icon-img"
              draggable={false}
            />
          </button>
          <button
            className="mobile-header__message-icon"
            onClick={handleMessageClick}
            aria-label={t("common.messageCenter")}
          >
            <img
              src={ICON_URLS.message}
              alt=""
              className="mobile-header__auth-bar-icon-img"
              draggable={false}
            />
          </button>
          <button
            className="mobile-header__service-icon"
            onClick={handleServiceClick}
            aria-label={t("profile.support")}
          >
            <img
              src={ICON_URLS.service}
              alt=""
              className="mobile-header__auth-bar-icon-img"
              draggable={false}
            />
          </button>
        </div>
      );
    }

    // 其他页面未登录时：显示登录按钮 + 搜索、客服 icon
    return (
      <>
        <Button
          variant="primary"
          size="sm"
          className="mobile-header__register-login-btn"
          onClick={() => openLoginRegisterPopup({ defaultMode: "register" })}
        >
          {t("auth.registerLogin")}
        </Button>
        <div className="mobile-header__right-icons">
          <button
            className="mobile-header__search-icon"
            onClick={handleSearchClick}
            aria-label="Search"
          >
            <img
              src={ICON_URLS.search}
              alt=""
              className="mobile-header__auth-bar-icon-img"
              draggable={false}
            />
          </button>
          <button
            className="mobile-header__service-icon"
            onClick={handleServiceClick}
            aria-label={t("profile.support")}
          >
            <img
              src={ICON_URLS.service}
              alt=""
              className="mobile-header__auth-bar-icon-img"
              draggable={false}
            />
          </button>
        </div>
      </>
    );
  };

  // 下载条 + 等高占位块（仅首页）。DownloadAppBar 自带路由/会话/App 三重显隐门；
  // __download-slot 在浮动顶栏内把 header 内容让到下载条下方（与 classic variant 同构）。
  const renderDownloadBar = () => (
    <>
      <DownloadAppBar />
      {downloadAppBarVisible && (
        <div className="mobile-header__download-slot" aria-hidden="true" />
      )}
    </>
  );

  // SuperAce 首页专用布局
  const renderSuperAceHomeHeader = () => {
    return (
      <div className="mobile-header__superace">
        {/* Logo */}
        <div className="mobile-header__superace-logo">
          {logoSrc && (
            <img
              src={logoSrc}
              alt="Logo"
              className="mobile-header__superace-logo-img"
              draggable={false}
            />
          )}
        </div>

        {/* 登录、注册按钮 */}
        <div className="mobile-header__superace-buttons">
          <Button
            variant="outline"
            size="md"
            className="mobile-header__login-btn"
            onClick={() => openLoginRegisterPopup({ defaultMode: "login" })}
          >
            {t("auth.login")}
          </Button>
          <div className="mobile-header__signup-btn-wrap">
            <PromoBubble
              text={homeRegisterPromoText}
              className="promo-bubble--below"
            />
            <Button
              variant="primary"
              size="md"
              className="mobile-header__signup-btn"
              onClick={() =>
                openLoginRegisterPopup({
                  defaultMode: "register",
                  defaultAuthMode: "register",
                })
              }
            >
              {t("auth.signUp")}
            </Button>
          </div>
        </div>

        {/* 右侧：促销票图标 */}
        <button
          className="mobile-header__promo-btn"
          aria-label="Promo"
          onClick={handlePromoClick}
        >
          {renderPromoIcon()}
        </button>
      </div>
    );
  };

  // 子页面使用简化的 header（返回按钮 + 标题 + 右侧icon）
  if (pageType === "subpage") {
    return (
      <header
        ref={headerRef}
        className={`mobile-header mobile-header--subpage ${className}`}
      >
        <div className="mobile-header__subpage">
          {/* 左侧：返回按钮或占位 */}
          {!hideBackButton ? (
            <button
              className="mobile-header__back-btn"
              onClick={() => routerNavigate(-1)}
              aria-label="Go back"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 36 30"
                stroke="currentColor"
                strokeWidth="4.5"
                className="mobile-header__back-icon"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 15h30M15 3 3 15l12 12"
                />
              </svg>
            </button>
          ) : (
            <div className="mobile-header__back-placeholder" />
          )}
          {/* 中间：标题 */}
          <h1 className="mobile-header__subpage-title">{title}</h1>
          {/* 右侧：icon或占位 */}
          {rightIcon ? (
            <div className="mobile-header__right-icon">{rightIcon}</div>
          ) : (
            <div className="mobile-header__right-placeholder" />
          )}
        </div>
      </header>
    );
  }

  // 首页使用 SuperAce 布局
  if (pageType === "home" && !isAuthenticated) {
    return (
      <>
        {renderDownloadBar()}
        <header
          ref={headerRef}
          className={`mobile-header mobile-header--superace ${isScrolled ? "mobile-header--scrolled" : ""} ${className}`}
        >
          {renderSuperAceHomeHeader()}
        </header>
      </>
    );
  }

  return (
    <>
      {pageType === "home" && renderDownloadBar()}
      <header
        ref={headerRef}
        className={`mobile-header mobile-header--${pageType} ${isAuthenticated ? "mobile-header--authenticated" : ""} ${className}`}
      >
        <div className="mobile-header__content">
          <div className="mobile-header__auth-bar">
            <div className="mobile-header__logo">
              {logoSrc && (
                <img
                  src={logoSrc}
                  alt="Logo"
                  className="mobile-header__logo-img"
                  draggable={false}
                />
              )}
            </div>

            <div className="mobile-header__right-section">{renderIcons()}</div>
          </div>
        </div>
      </header>
    </>
  );
}

export default Header;
